import React, { useCallback, useState } from 'react';

import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { useAttachExperience } from '~/hooks/apis/accommodation/useBundles';

interface Props {
  bundleId: string;
  open: boolean;
  onClose: () => void;
}

function AccommodationBundleAttachExperienceDialog({ bundleId, open, onClose }: Props) {
  const [experienceId, setExperienceId] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const attachExperienceMutation = useAttachExperience();

  const handleSubmit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      setErrorMessage(null);
      try {
        await attachExperienceMutation.mutateAsync({
          bundleId,
          payload: { experienceId: experienceId.trim() },
        });
        onClose();
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : 'Failed to attach experience';
        setErrorMessage(msg);
      }
    },
    [attachExperienceMutation, bundleId, experienceId, onClose],
  );

  const isSubmitting = attachExperienceMutation.isPending;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>Attach Experience</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <Typography variant="body2" color="text.secondary">
              Enter the svc-experience ID. The server will fetch experience metadata (name, description, icon) and
              attach it to this bundle as an EXPERIENCE offer.
            </Typography>
            <TextField
              label="Experience ID"
              value={experienceId}
              onChange={(e) => setExperienceId(e.target.value)}
              required
              fullWidth
              autoFocus
            />
            {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={!experienceId.trim() || isSubmitting}>
            {isSubmitting ? 'Attaching…' : 'Attach'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

export default AccommodationBundleAttachExperienceDialog;
